/**
 * Editor HTML basato su TipTap (RF-03a / D2).
 * - Output HTML via editor.getHTML() (salvato in body_html).
 * - Toolbar MUI + menu placeholder dinamici (§11.5).
 * - StarterKit v3 include Link: lo si disabilita lì e si usa l'estensione Link
 *   standalone per controllarne il comportamento (openOnClick: false).
 *
 * Riferimento: TDD Sprint 18 §8.3.
 */
import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, FormHelperText } from '@mui/material';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import TextAlign from '@tiptap/extension-text-align';
import { EditorToolbar } from './EditorToolbar';
import { PlaceholderMenu } from './PlaceholderMenu';
import { EmailButton } from './EmailButton';
import { EmailContainer, InlineStyle } from './EmailFormatting';

/**
 * Separatore orizzontale tra sezioni: linea sottile al 90% della larghezza del corpo, centrata.
 * Stile inline perché i client di posta ignorano il CSS esterno; `width` e `align` anche come
 * attributi HTML, che Outlook rispetta più del CSS. Il bordo superiore (non il fondo dell'<hr>)
 * evita la resa "incavata" a doppia linea che alcuni client applicano di default.
 */
const HORIZONTAL_RULE_ATTRIBUTES = {
  width: '90%',
  align: 'center',
  style: 'border:0;border-top:1px solid #dfe3e8;height:0;width:90%;margin:24px auto;',
};

/**
 * @param {{
 *   value?: string,
 *   onChange?: (html: string) => void,
 *   error?: boolean,
 *   helperText?: string,
 *   disabled?: boolean,
 *   placeholder?: string,
 *   minHeight?: number|string,
 * }} props
 */
export function HtmlEditor({
  value = '',
  onChange,
  error,
  helperText,
  disabled = false,
  placeholder = 'Scrivi il contenuto della newsletter…',
  minHeight = 240,
  entityId,
  extraInserts,
}) {
  const editor = useEditor({
    editable: !disabled,
    extensions: [
      StarterKit.configure({
        link: false,
        horizontalRule: { HTMLAttributes: HORIZONTAL_RULE_ATTRIBUTES },
      }),
      Link.configure({ openOnClick: false, autolink: true }),
      // style fisso su ogni immagine: nelle email serve che non sfondino la larghezza del
      // messaggio su schermi stretti, e che l'altezza resti proporzionata al ridimensionamento.
      // La larghezza scelta viaggia invece come attributo HTML `width`, che i client di posta
      // (Outlook in testa) rispettano molto più del CSS.
      //
      // `inline: true` rende l'immagine parte del paragrafo che la contiene: è ciò che le
      // permette di seguire l'allineamento. Da nodo a sé stante, il text-align non avrebbe
      // alcun effetto su un <img>.
      Image.configure({
        inline: true,
        HTMLAttributes: { style: 'max-width:100%;height:auto;' },
      }),
      EmailButton,
      // Senza queste due, aprire un template impaginato lo ridurrebbe a testo nudo:
      // i contenitori verrebbero scartati e gli stili inline persi.
      EmailContainer,
      InlineStyle,
      // Allineamento come stile inline sul blocco: i client di posta non leggono CSS esterno.
      // Incluso il bottone CTA, il cui contenitore riceve così l'allineamento scelto.
      TextAlign.configure({
        types: ['heading', 'paragraph', 'emailButton'],
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: value,
    onUpdate: ({ editor: ed }) => onChange?.(ed.getHTML()),
  });

  // Sincronizza il contenuto quando `value` cambia dall'esterno (es. selezione template),
  // evitando di sovrascrivere mentre l'utente sta scrivendo.
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if (value !== current && value !== undefined) {
      editor.commands.setContent(value || '', { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  // Allinea lo stato editable se cambia `disabled`.
  useEffect(() => {
    if (editor) editor.setEditable(!disabled);
  }, [disabled, editor]);

  return (
    <Box>
      <Box
        sx={{
          border: `1px solid ${error ? '#d32f2f' : '#c4c4c4'}`,
          borderRadius: 1,
          '& .ProseMirror': {
            minHeight,
            padding: '12px 14px',
            outline: 'none',
            overflowWrap: 'anywhere',
          },
          // Il reset globale azzera il padding di ul/ol: lo ripristiniamo così i marker
          // (bullet/numeri) restano dentro il bordo del box editor.
          '& .ProseMirror ul, & .ProseMirror ol': {
            paddingLeft: '1.5rem',
            margin: '0.5rem 0',
            listStylePosition: 'outside',
          },
          '& .ProseMirror li': { marginBottom: '0.25rem' },
          '& .ProseMirror li > p': { margin: 0 },
          // Gli stili globali dell'applicazione impostano `img { display: block }`: così
          // l'immagine ignorerebbe il text-align del paragrafo e non sarebbe allineabile.
          // Qui dentro la si riporta a inline-block, che resta allineabile e mantiene
          // larghezza e altezza impostate.
          '& .ProseMirror img': { display: 'inline-block' },
          // Immagine selezionata: deve essere evidente su quale si stanno applicando le misure.
          '& .ProseMirror img.ProseMirror-selectednode': {
            outline: '2px solid #1976d2',
            outlineOffset: '2px',
          },
          // Separatore selezionato: evidenziato come immagini e bottoni, così si vede cosa si cancella.
          '& .ProseMirror hr': { cursor: 'pointer' },
          '& .ProseMirror hr.ProseMirror-selectednode': {
            outline: '2px solid #1976d2',
            outlineOffset: '4px',
          },
          // Bottone CTA: dentro l'editor il click deve selezionare il nodo, non seguire il link.
          '& .ProseMirror [data-email-button] a': { pointerEvents: 'none' },
          '& .ProseMirror [data-email-button]': { cursor: 'pointer' },
          '& .ProseMirror [data-email-button].ProseMirror-selectednode': {
            outline: '2px solid #1976d2',
            outlineOffset: '2px',
            borderRadius: '4px',
          },
          '& .ProseMirror p.is-editor-empty:first-of-type::before': {
            content: 'attr(data-placeholder)',
            color: '#9e9e9e',
            float: 'left',
            height: 0,
            pointerEvents: 'none',
          },
        }}
      >
        {!disabled && (
          <>
            <EditorToolbar editor={editor} disabled={disabled} entityId={entityId} />
            <Box sx={{ px: 0.5, borderBottom: '1px solid #eee' }}>
              <PlaceholderMenu editor={editor} disabled={disabled} extraItems={extraInserts} />
            </Box>
          </>
        )}
        <EditorContent editor={editor} />
      </Box>
      {helperText && <FormHelperText error={error}>{helperText}</FormHelperText>}
    </Box>
  );
}

HtmlEditor.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  minHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  // Entità mittente: la libreria file la usa per registrare i nuovi caricamenti.
  entityId: PropTypes.string,
  // Blocchi aggiuntivi nel menu "Inserisci campo" (es. spazio contenuto nei template).
  extraInserts: PropTypes.arrayOf(PropTypes.object),
};

export default HtmlEditor;